import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Target, Heart, Users, TrendingUp, Award, Leaf, Clock, Handshake, Pencil, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OrgLeaderboard from '../components/shared/OrgLeaderboard';
import axios, { API } from '../utils/api';

// Metric-id → lucide icon. Kept as a plain lookup so the edit page,
// which never picks an icon, doesn't need to know about this map.
const METRIC_ICON = {
  water_litres: Leaf, paper_kg: Leaf, plastic_kg: Leaf, ewaste_kg: Leaf,
  co2e_saved_water: Leaf, co2e_saved_paper: Leaf, co2e_saved_plastic: Leaf, co2e_saved_ewaste: Leaf,
  beneficiaries_reached: Users, volunteer_hours: Clock, steps: TrendingUp,
};
const iconForMetric = (mid) => METRIC_ICON[mid] || Target;

// Icon rotation for commitment cards — HR doesn't pick icons, we cycle
// three visually-distinct ones so consecutive cards don't look identical.
const COMMITMENT_ICONS = [Award, Heart, Handshake, Leaf, Users];

const OrganizationPage = ({ BottomNav }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHRAdmin = user?.role === 'HR_ADMIN';

  const [targets, setTargets] = useState(null);       // null = loading, [] = configured-but-empty
  const [commitments, setCommitments] = useState(null);
  const [org, setOrg] = useState(null);                // null = loading, {} = fetch failed
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tRes, oRes] = await Promise.allSettled([
          axios.get(`${API}/org/targets`),
          axios.get(`${API}/organizations/current`),
        ]);
        if (cancelled) return;
        const tData = tRes.status === 'fulfilled' ? tRes.value.data : null;
        const oData = oRes.status === 'fulfilled' ? oRes.value.data : null;
        setTargets(Array.isArray(tData) ? tData : []);
        setCommitments(Array.isArray(oData?.commitments) ? oData.commitments : []);
        setOrg(oData || {});
      } catch {
        if (cancelled) return;
        setTargets([]);
        setCommitments([]);
        setOrg({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Theme colors
  const THEME = {
    primary: '#5DBFAD',
    secondary: '#73C8B9',
    softBg: 'rgba(180, 225, 216, 0.1)',
    accent: '#FBC98A',
    text: '#1F2937',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
    border: '#E5E7EB',
    surface: '#F9FAFB',
    white: '#FFFFFF'
  };

  const orgLoading = org == null;
  const orgName = (org?.name || '').trim() || 'SocialStar';
  const orgLogoSrc = org?.logo_url && !logoFailed ? org.logo_url : '/logo-symbol.png';
  const usingOrgLogo = orgLogoSrc !== '/logo-symbol.png';

  // Animated line graph SVG component
  const AnimatedLineGraph = () => (
    <svg width="100%" height="80" viewBox="0 0 300 80" fill="none" style={{ marginTop: 16 }}>
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={THEME.primary} stopOpacity="0.2" />
          <stop offset="50%" stopColor={THEME.primary} stopOpacity="0.4" />
          <stop offset="100%" stopColor={THEME.primary} stopOpacity="0.2" />
        </linearGradient>
      </defs>
      {/* Grid lines */}
      <line x1="0" y1="20" x2="300" y2="20" stroke={THEME.border} strokeWidth="1" strokeDasharray="4" />
      <line x1="0" y1="40" x2="300" y2="40" stroke={THEME.border} strokeWidth="1" strokeDasharray="4" />
      <line x1="0" y1="60" x2="300" y2="60" stroke={THEME.border} strokeWidth="1" strokeDasharray="4" />
      {/* Animated path */}
      <motion.path
        d="M0 60 L50 50 L100 55 L150 35 L200 40 L250 25 L300 20"
        stroke={THEME.primary}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, ease: "easeInOut" }}
      />
      {/* Data points */}
      {[
        { x: 0, y: 60 },
        { x: 50, y: 50 },
        { x: 100, y: 55 },
        { x: 150, y: 35 },
        { x: 200, y: 40 },
        { x: 250, y: 25 },
        { x: 300, y: 20 }
      ].map((point, i) => (
        <motion.circle
          key={i}
          cx={point.x}
          cy={point.y}
          r="4"
          fill={THEME.white}
          stroke={THEME.primary}
          strokeWidth="2"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3 * i, duration: 0.3 }}
        />
      ))}
    </svg>
  );

  // Animated bar chart SVG
  const AnimatedBarChart = () => (
    <svg width="100%" height="100" viewBox="0 0 200 100" fill="none" style={{ marginTop: 12 }}>
      {[
        { x: 20, height: 60 },
        { x: 55, height: 80 },
        { x: 90, height: 45 },
        { x: 125, height: 70 },
        { x: 160, height: 55 }
      ].map((bar, i) => (
        <motion.rect
          key={i}
          x={bar.x}
          y={100 - bar.height}
          width="25"
          height={bar.height}
          rx="4"
          fill={THEME.primary}
          fillOpacity={0.2 + (i * 0.15)}
          stroke={THEME.primary}
          strokeWidth="1.5"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ delay: i * 0.15, duration: 0.5 }}
          style={{ transformOrigin: 'bottom' }}
        />
      ))}
    </svg>
  );

  // Circular progress ring
  const CircularProgress = ({ progress, size = 60 }) => {
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (progress / 100) * circumference;

    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={THEME.border}
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={THEME.primary}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </svg>
    );
  };

  return (
    <div className="page-container" style={{ background: THEME.white, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: `1px solid ${THEME.border}`,
        background: THEME.white,
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <button 
          onClick={() => navigate(-1)} 
          style={{ 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer',
            padding: 4,
            marginRight: 12,
            color: THEME.text
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
          {orgLoading ? (
            <div data-testid="org-page-name-placeholder" style={{ height: 24 }} />
          ) : (
            <div
              data-testid="org-page-name"
              style={{ fontSize: 18, fontWeight: 600, color: THEME.text, lineHeight: 1.3, overflowWrap: 'anywhere' }}
            >
              {orgName}
            </div>
          )}
        </div>
        {orgLoading ? (
          <div data-testid="org-page-logo-placeholder" style={{ width: 48, height: 48, flexShrink: 0 }} />
        ) : usingOrgLogo ? (
          <img
            src={orgLogoSrc}
            alt={orgName}
            data-testid="org-page-logo"
            onError={() => setLogoFailed(true)}
            style={{ height: 48, width: 'auto', maxWidth: 160, objectFit: 'contain', borderRadius: 0, flexShrink: 0 }}
          />
        ) : (
          <img
            src="/logo-symbol.png"
            alt="SocialStar"
            data-testid="org-page-logo-default"
            style={{ width: 36, height: 36, flexShrink: 0 }}
          />
        )}
      </div>

      <div style={{ padding: '20px', maxWidth: 600, margin: '0 auto' }}>
        {/* Impact Overview with Animated Graph */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            padding: '20px',
            background: THEME.softBg,
            borderRadius: 12,
            border: `1px solid ${THEME.border}`,
            marginBottom: 24
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <TrendingUp size={18} color={THEME.primary} strokeWidth={1.5} />
            <span style={{ fontSize: 13, fontWeight: 600, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Collective Impact Growth
            </span>
          </div>
          <AnimatedLineGraph />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Jan</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Mar</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>May</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Jul</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Sep</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Nov</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Dec</span>
          </div>
        </motion.div>

        {/* Organization Targets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ marginBottom: 24 }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 14
          }}>
            <Target size={18} color={THEME.primary} strokeWidth={1.5} />
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: THEME.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              flex: 1
            }}>
              Organization Targets
            </span>
            {isHRAdmin && (
              <button
                data-testid="edit-org-targets-btn"
                onClick={() => navigate('/organization/edit')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', borderRadius: 8,
                  background: THEME.softBg, border: `1px solid ${THEME.border}`,
                  color: THEME.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer'
                }}
              >
                <Pencil size={12} strokeWidth={2} /> Edit
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {targets === null ? (
              <div data-testid="targets-loading" style={{ padding: 20, background: THEME.white, borderRadius: 10, border: `1px solid ${THEME.border}`, textAlign: 'center', fontSize: 13, color: THEME.textMuted }}>
                Loading targets…
              </div>
            ) : targets.length === 0 ? (
              <div
                data-testid="targets-empty-state"
                style={{
                  padding: '20px 18px', background: THEME.white, borderRadius: 10,
                  border: `1px dashed ${THEME.border}`, textAlign: 'center'
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 500, color: THEME.text, marginBottom: 6 }}>
                  No organization targets configured yet
                </div>
                <div style={{ fontSize: 12, color: THEME.textSecondary, marginBottom: isHRAdmin ? 14 : 0 }}>
                  {isHRAdmin
                    ? 'Set your first target to track progress against real logged data.'
                    : 'Your HR admin hasn\u2019t set any targets for this organization yet.'}
                </div>
                {isHRAdmin && (
                  <button
                    data-testid="add-first-target-btn"
                    onClick={() => navigate('/organization/edit')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 8, background: THEME.primary,
                      border: 'none', color: THEME.white, fontSize: 12, fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    <Plus size={14} strokeWidth={2.5} /> Add first target
                  </button>
                )}
              </div>
            ) : (
              targets.map((target, index) => {
                const Icon = iconForMetric(target.metric_id);
                return (
                  <motion.div
                    key={target.metric_id}
                    data-testid={`org-target-row-${target.metric_id}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      padding: '16px',
                      background: THEME.white,
                      borderRadius: 10,
                      border: `1px solid ${THEME.border}`
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <CircularProgress progress={target.progress_pct} size={56} />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: 12,
                        fontWeight: 600,
                        color: THEME.primary
                      }}>
                        {target.progress_pct}%
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 4
                      }}>
                        <Icon size={16} color={THEME.textSecondary} strokeWidth={1.5} />
                        <span style={{ fontSize: 14, fontWeight: 500, color: THEME.text }}>
                          {target.label}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: THEME.textSecondary }}>
                        {Number(target.current_value || 0).toLocaleString()} / {Number(target.target).toLocaleString()} {target.unit || ''}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.div>

        {/* Our Commitments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ marginBottom: 24 }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 14
          }}>
            <Heart size={18} color={THEME.primary} strokeWidth={1.5} />
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: THEME.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              flex: 1
            }}>
              Our Commitments
            </span>
            {isHRAdmin && (
              <button
                data-testid="edit-org-commitments-btn"
                onClick={() => navigate('/organization/edit')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', borderRadius: 8,
                  background: THEME.softBg, border: `1px solid ${THEME.border}`,
                  color: THEME.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer'
                }}
              >
                <Pencil size={12} strokeWidth={2} /> Edit
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {commitments === null ? (
              <div data-testid="commitments-loading" style={{ padding: 20, background: THEME.white, borderRadius: 10, border: `1px solid ${THEME.border}`, textAlign: 'center', fontSize: 13, color: THEME.textMuted }}>
                Loading commitments…
              </div>
            ) : commitments.length === 0 ? (
              <div
                data-testid="commitments-empty-state"
                style={{
                  padding: '20px 18px', background: THEME.white, borderRadius: 10,
                  border: `1px dashed ${THEME.border}`, textAlign: 'center'
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 500, color: THEME.text, marginBottom: 6 }}>
                  No commitments published yet
                </div>
                <div style={{ fontSize: 12, color: THEME.textSecondary, marginBottom: isHRAdmin ? 14 : 0 }}>
                  {isHRAdmin
                    ? 'Publish up to 5 commitment cards to share your CSR pledge with the team.'
                    : 'Your HR admin hasn\u2019t published any commitments yet.'}
                </div>
                {isHRAdmin && (
                  <button
                    data-testid="add-first-commitment-btn"
                    onClick={() => navigate('/organization/edit')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 8, background: THEME.primary,
                      border: 'none', color: THEME.white, fontSize: 12, fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    <Plus size={14} strokeWidth={2.5} /> Add first commitment
                  </button>
                )}
              </div>
            ) : (
              commitments.map((commitment, index) => {
                const Icon = COMMITMENT_ICONS[index % COMMITMENT_ICONS.length];
                return (
                  <motion.div
                    key={index}
                    data-testid={`org-commitment-row-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                      padding: '14px 16px',
                      background: THEME.white,
                      borderRadius: 10,
                      border: `1px solid ${THEME.border}`
                    }}
                  >
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: THEME.softBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon size={18} color={THEME.primary} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: THEME.text, marginBottom: 4 }}>
                        {commitment.title}
                      </div>
                      <div style={{ fontSize: 12, color: THEME.textSecondary, lineHeight: 1.4 }}>
                        {commitment.description}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.div>

        {/* Participation Stats with Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{
            padding: '20px',
            background: THEME.surface,
            borderRadius: 12,
            border: `1px solid ${THEME.border}`,
            marginBottom: 24
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Users size={18} color={THEME.primary} strokeWidth={1.5} />
            <span style={{ fontSize: 13, fontWeight: 600, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Weekly Participation
            </span>
          </div>
          <AnimatedBarChart />
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 8 }}>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Mon</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Tue</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Wed</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Thu</span>
            <span style={{ fontSize: 11, color: THEME.textMuted }}>Fri</span>
          </div>
        </motion.div>

        {/* Leaderboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          style={{ marginBottom: 100 }}
        >
          <OrgLeaderboard currentUserId={user?.id} initialVisible={5} />
        </motion.div>
      </div>

      <BottomNav active="home" />
    </div>
  );
};

export default OrganizationPage;
